"use client"

import React, { useEffect, useRef, useState } from 'react'
import { cubicBezier } from 'framer-motion';

import Image, { StaticImageData } from "next/image";
import TrustedPartners from "./TrustedPartners";
import RotatingSocialIcons from './otherpages/GrowsuiteRotatingSocialIcons';

import GoogleImg from '@/images/G_image.webp';
import GlobalPartnersCount from '@/components/GlobalPartnersCount';

// Removed 6 static image imports that pointed at public/images/{fintech,mobile-img/fintech}/ —
// files a past image cleanup deleted. They were only referenced by commented-out JSX, so the
// build elided them silently; un-commenting that JSX would have broken it. Live code uses the CDN.
import { ReviewStar as Icon_ReviewStar } from "@/components/customer/icons/ReviewStar";
const SvgIcons = { ReviewStar: Icon_ReviewStar };

// import demo_test1 from '../../public/images/testimonials/testi_demo (1).webp';
import demo_test1 from '@/public/images/testimonials/testi_demo (1).webp';
import { useInView } from 'framer-motion';
import { m } from 'framer-motion';
;
import FaqSection from './FaqSection';
import { getImageUrl } from '@/lib/imageUrl';
import TestiReviewCommonImages from './otherpages/TestiReviewCommonImages';

type Props = {
  desktop: string | StaticImageData
  mobile: string | StaticImageData
}

const logos = [
  { src: getImageUrl("/vilvabusiness/website/products/fintech/coursole/img (1).webp"), alt: "HDFC Bank" },
  { src: getImageUrl("/vilvabusiness/website/products/fintech/coursole/img (2).webp"), alt: "Castler" },
  { src: getImageUrl("/vilvabusiness/website/products/fintech/coursole/img (3).webp"), alt: "PaySprint" },
  { src: getImageUrl("/vilvabusiness/website/products/fintech/coursole/img (4).webp"), alt: "Karur Vysya Bank" },
  { src: getImageUrl("/vilvabusiness/website/products/fintech/coursole/img (5).webp"), alt: "Axis Bank" },
  { src: getImageUrl("/vilvabusiness/website/products/fintech/coursole/img (6).webp"), alt: "CSB Bank" },
];

const partners = [
  { src: getImageUrl("/vilvabusiness/website/products/fintech/equitees.webp"), alt: "Partner 1" },
  { src: getImageUrl("/vilvabusiness/website/products/fintech/suryoday.webp"), alt: "Partner 2" },
  { src: getImageUrl("/vilvabusiness/website/products/fintech/ujavan.webp"), alt: "Partner 3" },
  { src: getImageUrl("/vilvabusiness/website/products/fintech/au.webp"), alt: "Partner 4" },
];


// FAQs for the fintech page. These previously held the DIGITAL MARKETING FAQ set, which was
// wrong for this page and confusing for anyone landing here from search.
const faqs = [
  {
    question: "What fintech services does Vilva Business offer?",
    answer: "We build and support fintech products end to end: customer and admin portals, business intelligence dashboards, digital onboarding suites, and disbursal and collection systems — alongside consulting for PA/PG licensing, NBFC onboarding, and P2P lending compliance."
  },
  {
    question: "Do you help with Payment Aggregator (PA) and Payment Gateway (PG) licensing?",
    answer: "Yes. We guide you through the licensing journey — documentation, technical and security readiness, audit preparation, and coordination with banking partners. Final approval always rests with the regulator, so we prepare you to meet the requirements rather than promise an outcome."
  },
  {
    question: "Can you support NBFC onboarding and compliance?",
    answer: "Yes. We provide NBFC onboarding and consulting together with the technology behind it — loan origination, disbursal and collections, reporting, and the audit trails your compliance team needs."
  },
  {
    question: "Do you work with P2P lending platforms?",
    answer: "Yes. We advise on P2P lending compliance and build the supporting workflows, from borrower and lender onboarding through disbursal, repayment tracking, and reporting."
  },
  {
    question: "What is included in your digital onboarding suite?",
    answer: "KYC and eKYC journeys, document capture and verification, real-time validation of customers, sellers and delivery partners, and complete audit trails — so you can approve genuine users quickly while keeping evidence for every decision."
  },
  {
    question: "Do you provide cybersecurity and audit support?",
    answer: "Yes. We carry out cybersecurity assessments and DLSIR audits, then work with your team on remediation before go-live, so security gaps are closed rather than just documented."
  },
  {
    question: "How quickly can we go live?",
    answer: "Our modules are ready to deploy, which typically halves a conventional build timeline. The honest answer is that the critical path is usually banking-partner onboarding and regulatory approval rather than the software, so we scope realistic dates with you up front."
  },
  {
    question: "Which banking and escrow partners do you integrate with?",
    answer: "We integrate with leading banks, NBFCs and escrow providers, and our team has delivered live integrations across payments, escrow, and disbursal rails. We will confirm the exact partners available for your use case during the discovery call."
  },
  {
    question: "Who do you build fintech solutions for?",
    answer: "Fintech startups, NBFCs, lending platforms, payment businesses, and marketplaces that need to validate users and move money safely — from first product launch through to scaling an existing platform."
  },
  {
    question: "What support do you provide after launch?",
    answer: "Ongoing engineering support, monitoring, and business intelligence dashboards so you can track transactions and performance — plus continued compliance support as regulations and your product change."
  }
];

const complianceFeatures = [
  {
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
      <path fillRule="evenodd" clipRule="evenodd" d="M22.259 16.4726L30.4803 1.41908C30.5834 1.2303 30.4467 1 30.2316 1H16.872C16.7546 1 16.6494 1.0724 16.6074 1.18204L9.50726 19.7361C9.43627 19.9216 9.57323 20.1206 9.77184 20.1206H16.698V20.1237C17.0936 20.1231 17.4147 20.4433 17.4153 20.8389C17.4154 20.9133 17.404 20.9872 17.3813 21.058L11.772 38.6288C11.6768 38.9272 12.0646 39.1367 12.2619 38.8935L29.3486 17.8329C29.501 17.6451 29.363 17.365 29.1212 17.3713L22.917 17.5332C22.5213 17.549 22.1876 17.2411 22.1717 16.8453C22.1665 16.7155 22.1967 16.5866 22.259 16.4726Z" fill="#418AF1" />
    </svg>,
    title: "Ready-to-Deploy Components",
    description: "Prebuilt components can reduce development work; delivery timelines depend on project scope, integrations, and approvals.",
  },
  {
    icon:
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
        <path d="M36.218 21.8666C34.7534 21.4908 33.334 20.9571 31.9846 20.275C31.568 20.0666 31.1846 19.8666 30.8346 19.675C30.143 19.2916 29.593 18.95 29.2346 18.7166C29.2142 18.7022 29.1917 18.691 29.168 18.6833C28.9038 18.5293 28.6024 18.451 28.2966 18.4569C27.9909 18.4627 27.6927 18.5526 27.4346 18.7166C25.2845 20.1227 22.9285 21.1854 20.4513 21.8666C20.0866 21.9552 19.7621 22.1634 19.5295 22.4579C19.2969 22.7524 19.1696 23.1163 19.168 23.4916V24.9166C19.1671 26.9746 19.6748 29.0009 20.646 30.8153C21.6172 32.6297 23.0217 34.176 24.7346 35.3166L25.5096 35.8333L27.4096 37.1C27.5603 37.1992 27.7264 37.2724 27.9013 37.3166H27.9096C28.0463 37.3637 28.1901 37.3863 28.3346 37.3833C28.6639 37.3805 28.9853 37.282 29.2596 37.1L31.9346 35.3166C33.6476 34.176 35.0521 32.6297 36.0233 30.8153C36.9945 29.0009 37.5022 26.9746 37.5013 24.9166V23.4916C37.4996 23.1163 37.3723 22.7524 37.1398 22.4579C36.9072 22.1634 36.5826 21.9552 36.218 21.8666ZM31.4263 26.425L28.093 29.7583C27.935 29.9132 27.7226 30 27.5013 30C27.28 30 27.0676 29.9132 26.9096 29.7583L25.243 28.0916C25.0861 27.9347 24.9979 27.7219 24.9979 27.5C24.9979 27.278 25.0861 27.0652 25.243 26.9083C25.3999 26.7514 25.6127 26.6632 25.8346 26.6632C26.0566 26.6632 26.2694 26.7514 26.4263 26.9083L27.5013 27.9916L30.243 25.2416C30.3207 25.1639 30.4129 25.1023 30.5144 25.0602C30.6159 25.0182 30.7248 24.9966 30.8346 24.9966C30.9445 24.9966 31.0533 25.0182 31.1548 25.0602C31.2564 25.1023 31.3486 25.1639 31.4263 25.2416C31.504 25.3193 31.5656 25.4116 31.6077 25.5131C31.6497 25.6146 31.6714 25.7234 31.6714 25.8333C31.6714 25.9432 31.6497 26.052 31.6077 26.1535C31.5656 26.255 31.504 26.3473 31.4263 26.425Z" fill="#418AF1" />
        <path d="M29.65 11.0333L22.525 3.74999C22.2693 3.4912 21.9807 3.26707 21.6667 3.08332V9.16665C21.6686 9.82909 21.9327 10.4638 22.4011 10.9322C22.8695 11.4006 23.5042 11.6647 24.1667 11.6667H30.1583C30.008 11.4409 29.8379 11.2289 29.65 11.0333ZM30.7833 13.3333H24.1667C23.0616 13.3333 22.0018 12.8943 21.2204 12.1129C20.439 11.3315 20 10.2717 20 9.16665V2.53332C19.8512 2.50979 19.7007 2.49864 19.55 2.49999H6.66667C5.5616 2.49999 4.50179 2.93897 3.72039 3.72037C2.93899 4.50178 2.5 5.56158 2.5 6.66665V33.3333C2.5 34.4384 2.93899 35.4982 3.72039 36.2796C4.50179 37.061 5.5616 37.5 6.66667 37.5H25.0083L23.8083 36.7C21.8667 35.4084 20.2746 33.6567 19.1739 31.6008C18.0733 29.5449 17.4982 27.2486 17.5 24.9167V23.4917C17.5003 22.9763 17.6201 22.468 17.85 22.0067C18.0799 21.5455 18.4137 21.1438 18.825 20.8333H8.33333C8.11232 20.8333 7.90036 20.7455 7.74408 20.5892C7.5878 20.433 7.5 20.221 7.5 20C7.5 19.779 7.5878 19.567 7.74408 19.4107C7.90036 19.2544 8.11232 19.1667 8.33333 19.1667H23.1167C24.2967 18.6345 25.4359 18.0162 26.525 17.3167C27.0136 17.0062 27.574 16.8271 28.1521 16.7964C28.7301 16.7657 29.3063 16.8847 29.825 17.1417L29.8833 17.1667L30.1333 17.3167C30.3083 17.4333 30.5417 17.575 30.8333 17.75V13.95C30.8336 13.7434 30.8169 13.5372 30.7833 13.3333ZM17.5 30C17.5 30.221 17.4122 30.433 17.2559 30.5892C17.0996 30.7455 16.8877 30.8333 16.6667 30.8333H8.33333C8.11232 30.8333 7.90036 30.7455 7.74408 30.5892C7.5878 30.433 7.5 30.221 7.5 30C7.5 29.779 7.5878 29.567 7.74408 29.4107C7.90036 29.2544 8.11232 29.1667 8.33333 29.1667H16.6667C16.8877 29.1667 17.0996 29.2544 17.2559 29.4107C17.4122 29.567 17.5 29.779 17.5 30ZM8.33333 24.1667H15C15.221 24.1667 15.433 24.2544 15.5893 24.4107C15.7455 24.567 15.8333 24.779 15.8333 25C15.8333 25.221 15.7455 25.433 15.5893 25.5892C15.433 25.7455 15.221 25.8333 15 25.8333H8.33333C8.11232 25.8333 7.90036 25.7455 7.74408 25.5892C7.5878 25.433 7.5 25.221 7.5 25C7.5 24.779 7.5878 24.567 7.74408 24.4107C7.90036 24.2544 8.11232 24.1667 8.33333 24.1667ZM8.33333 9.16665H15C15.221 9.16665 15.433 9.25445 15.5893 9.41073C15.7455 9.56701 15.8333 9.77897 15.8333 9.99999C15.8333 10.221 15.7455 10.433 15.5893 10.5892C15.433 10.7455 15.221 10.8333 15 10.8333H8.33333C8.11232 10.8333 7.90036 10.7455 7.74408 10.5892C7.5878 10.433 7.5 10.221 7.5 9.99999C7.5 9.77897 7.5878 9.56701 7.74408 9.41073C7.90036 9.25445 8.11232 9.16665 8.33333 9.16665ZM20 15.8333H8.33333C8.11232 15.8333 7.90036 15.7455 7.74408 15.5892C7.5878 15.433 7.5 15.221 7.5 15C7.5 14.779 7.5878 14.567 7.74408 14.4107C7.90036 14.2544 8.11232 14.1667 8.33333 14.1667H20C20.221 14.1667 20.433 14.2544 20.5893 14.4107C20.7455 14.567 20.8333 14.779 20.8333 15C20.8333 15.221 20.7455 15.433 20.5893 15.5892C20.433 15.7455 20.221 15.8333 20 15.8333Z" fill="#418AF1" />
      </svg>,
    title: "Compliance-First Approach",
    description:
      "Stay audit-ready always with built-in regulatory expertise and certified audits.",
  },
  {
    icon:
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
        <path d="M3.33203 29.9999C3.33203 26.8573 3.33203 25.2859 4.30835 24.3096C5.28465 23.3333 6.856 23.3333 9.9987 23.3333C13.1414 23.3333 14.7127 23.3333 15.689 24.3096C16.6654 25.2859 16.6654 26.8573 16.6654 29.9999C16.6654 33.1426 16.6654 34.7139 15.689 35.6903C14.7127 36.6666 13.1414 36.6666 9.9987 36.6666C6.856 36.6666 5.28465 36.6666 4.30835 35.6903C3.33203 34.7139 3.33203 33.1426 3.33203 29.9999Z" fill="#418AF1" />
        <path fillRule="evenodd" clipRule="evenodd" d="M5.77281 5.77404C3.33203 8.2148 3.33203 12.1432 3.33203 19.9999C3.33203 20.6593 3.33203 21.2909 3.33348 21.8963C4.1803 21.3411 5.1134 21.0979 6.00803 20.9776C7.08406 20.8329 8.40683 20.8331 9.84957 20.8333H10.1479C11.5906 20.8331 12.9134 20.8329 13.9894 20.9776C15.1743 21.1369 16.4268 21.5118 17.4569 22.5418C18.4869 23.5719 18.8617 24.8243 19.021 26.0093C19.1657 27.0853 19.1655 28.4081 19.1654 29.8508V30.1491C19.1655 31.5919 19.1657 32.9146 19.021 33.9906C18.9007 34.8853 18.6577 35.8183 18.1024 36.6651C18.7079 36.6666 19.3394 36.6666 19.9987 36.6666C27.8554 36.6666 31.7839 36.6666 34.2245 34.2258C36.6654 31.7851 36.6654 27.8566 36.6654 19.9999C36.6654 12.1432 36.6654 8.2148 34.2245 5.77404C31.7839 3.33325 27.8554 3.33325 19.9987 3.33325C12.1419 3.33325 8.21358 3.33325 5.77281 5.77404ZM22.082 10.4166C21.3917 10.4166 20.832 10.9762 20.832 11.6666C20.832 12.3569 21.3917 12.9166 22.082 12.9166H25.3142L19.1149 19.1161C18.6267 19.6043 18.6267 20.3956 19.1149 20.8838C19.603 21.3719 20.3944 21.3719 20.8825 20.8838L27.082 14.6844V17.9166C27.082 18.6069 27.6417 19.1666 28.332 19.1666C29.0224 19.1666 29.582 18.6069 29.582 17.9166V11.6666C29.582 10.9762 29.0224 10.4166 28.332 10.4166H22.082Z" fill="#418AF1" />
      </svg>
    ,
    title: "E-commerce & Marketplaces",
    description: "Validate sellers, delivery partners, and customers in real time for safe, trustworthy transactions.",
  },
  {
    icon:
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
        <path fillRule="evenodd" clipRule="evenodd" d="M19.4148 3.43949C19.7922 3.298 20.2078 3.298 20.5852 3.43949L33.9185 8.43949C34.569 8.68342 35 9.3053 35 10V20.0929C35 25.7745 31.79 30.9684 26.7082 33.5094L20.7453 36.4907C20.2762 36.7254 19.7238 36.7254 19.2547 36.4907L13.2918 33.5094C8.21003 30.9684 5 25.7745 5 20.0929V10C5 9.3053 5.43095 8.68342 6.08147 8.43949L19.4148 3.43949ZM26.3015 17.7079C26.8765 16.989 26.76 15.9403 26.0412 15.3653C25.3223 14.7902 24.2735 14.9068 23.6985 15.6256L18.4032 22.2447L16.3525 19.3965C15.8147 18.6495 14.7731 18.48 14.0262 19.0179C13.2792 19.5557 13.1096 20.5972 13.6474 21.3442L16.9808 25.9739C17.2853 26.3969 17.7702 26.653 18.291 26.6662C18.812 26.6794 19.3092 26.4482 19.6348 26.0412L26.3015 17.7079Z" fill="#418AF1" />
      </svg>
    ,
    title: "Trusted Partnerships",
    description: "Backed by leading banks and NBFCs with secure, seamless integrations.",
  },
]


const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const leftItem = {
  hidden: { x: -100, opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: cubicBezier(0.42, 0, 0.58, 1), // Fixed
    },
  },
};

const upItem = {
  hidden: { y: 60, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: cubicBezier(0.42, 0, 0.58, 1), // Fixed
    },
  },
};

const rightItem = {
  hidden: { x: 100, opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: cubicBezier(0.42, 0, 0.58, 1), // Fixed
    },
  },
};

const logoItem = {
  hidden: { y: 40, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: cubicBezier(0.42, 0, 0.58, 1), // Fixed
    },
  },
};
const Fintech = () => {


  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);


  return (
    <div>

      <div className="min-h-screen bg-[linear-gradient(180deg,#FFF_17.6%,#48A8F4_100%)] relative overflow-hidden py-10 md:pb-[80px]">

        <div className="absolute inset-0 top-0 ">
          {/* The commented-out version that used to live here imported the background from
              public/images/fintech/, which a past image cleanup deleted. Removed along with its
              two now-dangling imports; this CDN copy is the one that renders. */}
          <Image
            src={getImageUrl("/vilvabusiness/website/products/fintech/global.webp")}
            alt="Fintech software development by Vilva Business"
            fill
            className="mix-blend-mode-darker"
            style={{ objectFit: "cover", objectPosition: "center" }}
            priority
            decoding="async"
          />
        </div>


        {/* Main Content */}
        <div className="relative z-10">
          <div className="flex flex-col items-centear text-center px-4 ">
            <div className="w-full space-y-[24px]">
              <div className="text-[#33C120] text-[16px] font-medium font-Schibsted_Grotesk">
                ⚡️ Get a Response within 24 Hours
              </div>
              <div>
                {/* Main Heading */}
                {/* <h1 className="md:text-[50px] text-[40px] leading-[120%] text-[#090D28] font-black max-w-3xl mx-auto"> */}
                <h1 className="font-NeueMontreal my-4 z-20 w-full relative md:w-[700px] lg:w-[1000px] mx-auto text-[#090D28]  text-center md:text-[68px] text-[40px] font-black leading-tight">
                  Fintech Software for Launching and Scaling Products
                </h1>

                {/* Subtitle */}
                <p className="text-[20px] text-[#476C88] schibsted-grotesk ">
                  Powerful portals, compliance audits, and lending solutions built for speed, trust, and exponential growth.
                </p>
              </div>

              {/* CTA Button */}
              <a href="/book-demo" aria-label="Book a Free Demo" className='mb-1'>
                <button className="bg-[#195AFF] mt-5 rounded-[8px] schibsted-grotesk text-white px-[28px] py-[14px]">
                  Book a Free Demo
                </button>
              </a>
            </div>
          </div>

          {/* Statistics Cards Section - Desktop: Horizontal layout, Mobile: 2x2 Grid */}
          <div className="px-4 lg:px-0 mb-6" ref={ref}>
            <div className="w-full max-w-[343px] lg:max-w-[960px] mx-auto">

              {/* Desktop: Horizontal Cards Layout */}
              <m.div
                variants={containerVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                className="hidden lg:flex lg:items-center lg:gap-4 lg:mb-4 mt-[100px]"
              >
                {/* Left Column - Two stacked cards (Left → Right) */}
                <div className="flex flex-col gap-[16px] w-[320px] ">
                  {/* 100K+ Card */}
                  <m.div variants={leftItem} className="bg-[#FFFFFF33] p-[2px] rounded-[12px]">
                    <div className="rounded-[12px] bg-gradient-to-b from-white to-[#D6ECFC] shadow-[-24px_146px_42px_0_rgba(116,177,216,0),-15px_94px_38px_0_rgba(116,177,216,0.01),-9px_53px_32px_0_rgba(116,177,216,0.05),-4px_23px_24px_0_rgba(116,177,216,0.09),-1px_6px_13px_0_rgba(116,177,216,0.10)] py-[14px] px-[20px] h-[112px] flex flex-col justify-center">
                      <div className="text-[40px] font-bold text-[#090D28] font-NeueMontreal">100K+</div>
                      <div className="text-[12px] font-normal text-[#000000]/60 leading-[140%] schibsted-grotesk">
                        Digital Onboardings simplified through our solutions
                      </div>
                    </div>
                  </m.div>

                  {/* 10+ Card */}
                  <m.div variants={leftItem} className="bg-[#FFFFFF33] p-[2px] rounded-[12px]">
                    <div className="bg-gradient-to-b from-white to-[#D6ECFC] rounded-xl shadow-lg h-[72px] flex justify-around items-center py-[14px] px-[20px]">
                      <div className="text-[40px] font-bold text-[#090D28] font-NeueMontreal">10+</div>
                      <div className="text-[12px] font-normal text-[#000000]/60 leading-[140%] schibsted-grotesk">
                        Clients in the last 5 years
                      </div>
                    </div>
                  </m.div>
                </div>

                {/* Implementation timeline card (Bottom → Top) */}
                <m.div variants={upItem} className="bg-[#FFFFFF33] p-[2px] rounded-[12px]">
                  <div className="bg-gradient-to-b from-white to-[#D6ECFC] rounded-xl px-[20px] py-[16px] shadow-lg w-[170px] h-[200px] flex flex-col justify-between">
                    <div className="text-[12px] font-normal text-[#000000]/60 leading-[140%] schibsted-grotesk">
                      Implementation support
                    </div>
                    <div className="text-[20px] font-bold text-[#090D28] font-NeueMontreal">Project-based</div>
                  </div>
                </m.div>

                {/* Compliance Card (Bottom → Top) */}
                <m.div variants={upItem} className="bg-[#FFFFFF33] p-[2px] rounded-[12px]">
                  <div className="bg-gradient-to-b from-white to-[#D6ECFC] rounded-xl px-[20px] py-[16px] shadow-lg w-[180px] h-[200px] flex flex-col justify-between">
                    <div>
                      <div className="text-[20px] font-bold text-[#090D28] font-NeueMontreal schibsted-grotesk">Compliance support</div>
                      {/* <div className="text-sm font-semibold text-[#090D28] schibsted-grotesk"></div> */}
                    </div>
                    <div className="text-[12px] font-normal text-[#000000]/60 leading-[140%] schibsted-grotesk">
                      Support for security and compliance requirements
                    </div>
                  </div>
                </m.div>

                {/* ₹0 to ₹20,000 CR Card (Right → Left) */}
                <m.div variants={rightItem} className="bg-[#FFFFFF33] p-[2px] rounded-[12px]">
                  <div className="bg-gradient-to-b from-white to-[#D6ECFC] rounded-xl px-[20px] py-[16px] shadow-lg w-full h-[200px] flex flex-col justify-between">
                    <div className="text-[12px] font-normal text-[#000000]/60 leading-[140%] schibsted-grotesk">
                      Scaling fintechs from
                    </div>
                    <div className="text-[40px] font-bold text-[#090D28] font-NeueMontreal leading-[100%]">₹0 to ₹20,000 CR</div>
                  </div>
                </m.div>
              </m.div>

              {/* Mobile: 2x2 Grid + Full Width Card */}
              <m.div
                variants={containerVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                className="grid grid-cols-2 gap-3 mb-3 lg:hidden"
              >
                <m.div variants={leftItem} className="bg-gradient-to-b from-white to-[#D6ECFC] rounded-xl p-[10px] shadow-lg">
                  <div className="text-[29px] font-bold text-[#090D28] mb-1 schibsted-grotesk">100K+</div>
                  <div className="text-[12px] font-normal text-[#000000]/60 leading-[140%] schibsted-grotesk">
                    Digital Onboardings simplified through our solutions
                  </div>
                </m.div>

                <m.div variants={leftItem} className="bg-[#FFFFFF33] p-[2px] rounded-[12px]">
                  <div className="bg-gradient-to-b from-white to-[#D6ECFC] h-full rounded-xl p-[10px] shadow-lg flex flex-col justify-center">
                    <div className="text-[29px] font-bold text-[#090D28] mb-1 font-NeueMontreal">10+</div>
                    <div className="text-[12px] font-normal text-[#000000]/60 leading-[140%] schibsted-grotesk">
                      Clients in the last 5 years
                    </div>
                  </div>
                </m.div>

                <m.div variants={upItem} className="bg-[#FFFFFF33] p-[2px] rounded-[12px]">
                  <div className="bg-gradient-to-b from-white to-[#D6ECFC] rounded-xl p-[10px] shadow-lg flex flex-col justify-between min-h-[147px]">
                    <div className="text-[12px] font-normal text-[#000000]/60 leading-[140%] schibsted-grotesk">
                      Implementation support
                    </div>
                    <div className="text-[18px] font-bold text-[#090D28] font-NeueMontreal">Project-based</div>
                  </div>
                </m.div>

                <m.div variants={upItem} className="bg-[#FFFFFF33] p-[2px] rounded-[12px]">
                  <div className="bg-gradient-to-b from-white to-[#D6ECFC] rounded-xl p-[10px] shadow-lg flex flex-col justify-between min-h-[147px]">
                    <div>
                      <div className="text-[18px] font-bold text-[#090D28] font-NeueMontreal schibsted-grotesk">Compliance support</div>
                    </div>
                    <div className="text-[12px] font-normal text-[#000000]/60 leading-[140%] schibsted-grotesk">
                      Support for security and compliance requirements
                    </div>
                  </div>
                </m.div>
              </m.div>

              {/* Mobile Scaling Card */}
              <m.div
                variants={rightItem}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                className="bg-gradient-to-b from-white to-[#D6ECFC] rounded-xl p-[10px] shadow-lg mb-3 lg:hidden "
              >
                <div className="flex flex-col gap-2 h-[70px] justify-between">
                  <div className="text-[12px] font-normal text-[#000000]/60 leading-[140%] schibsted-grotesk">Scaling fintechs from</div>
                  <div className="text-[29px] font-bold text-[#090D28] font-NeueMontreal leading-[100%]">₹0 to ₹20,000 CR</div>
                </div>
              </m.div>

              {/* Partners Section - Staggered Animation */}
              <m.div
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="bg-[#FFFFFF33] rounded-[8px] mt-4"
              >
                <div className="bg-gradient-to-b from-white to-[#D6ECFC] rounded-[8px] px-[20px] py-[14px] shadow-lg">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 lg:gap-[40px]">
                    <m.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={isInView ? { opacity: 1, y: 0 } : {}}
                      transition={{ duration: 0.6, delay: 0.6 }}
                      className="text-[14px] schibsted-grotesk text-[#696969] font-medium text-center lg:text-left lg:w-[155px]"
                    >
                      Trusted by Leading Banks & Partners
                    </m.div>

                    <m.div
                      variants={containerVariants}
                      initial="hidden"
                      animate={isInView ? "visible" : "hidden"}
                      className="flex flex-wrap justify-center lg:justify-between items-center gap-6 lg:gap-10"
                    >
                      {partners.map((partner, index) => (
                        <m.div key={index} variants={logoItem}>
                          <Image
                            src={partner.src}
                            alt={partner.alt}
                            width={120}
                            height={48}
                            className="h-8 lg:h-12 w-auto object-contain"
                            loading="lazy"
                            decoding="async"
                          />
                        </m.div>
                      ))}
                    </m.div>
                  </div>
                </div>
              </m.div>
            </div>
          </div>
        </div>
      </div>

      <TrustedPartners />

      <div className="min-h-screen bg-white lg:py-[100px] md:py-[80px] py-[40px] px-6 ">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-3xl md:text-4xl font-bold text-[#020D2C] leading-tight  tracking-wide max-w-2xl mx-auto font-NeueMontreal">
              Smart Solutions for the Next-Gen Financial Ecosystem
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 md:grid-cols-2  gap-6 mb-8 group/panel">
            <CustomerPortalsPanel />
            <BusinessIntelligencePanel />
            <DigitalOnboardingPanel />
            <AuditCompliancePanel />
            <ConsultingLicensingPanel isMobile={isMobile} />
            <DisbursalCollectionPanel />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 group/panel">

          </div>
        </div>
      </div>

      <div className="p-5 text-center bg-white">
        <h2 className="text-[#020D2C] font-semibold text-[24px] schibsted-grotesk">Trusted by Leading Escrow Partners</h2>
        <div className="max-w-7xl mx-auto grid grid-cols-3 md:grid-cols-6 gap-5 mt-5 justify-items-center">
          {logos.map((logo, index) => (
            <div key={index} className="flex-shrink-0">
              <Image src={logo.src} alt={logo.alt} width={150} height={50} />
            </div>
          ))}
        </div>
      </div>

      <section className="py-20 bg-[#F1F6F8]">
        <div className="max-w-[1400px] mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-[30px] text-[#020D2C] font-bold font-NeueMontreal">Built Different. Built for Fintech</h2>
            <p className="text-[#476C88] text-[16px] font-medium schibsted-grotesk">Powering faster launches, smarter scaling, and audit-ready compliance.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-2 border-[#D8E5EA] rounded-[8px]">
            {complianceFeatures.map((feature, index) => (
              <div
                key={index}
                className={`space-y-[8px] p-[24px] ${index < complianceFeatures.length - 1 ? 'md:border-r-2 border-r-none md:border-b-none border-b-2 border-[#D8E5EA]' : ''
                  }`}
              >
                <div className="text-white">{feature.icon}</div>
                <h3 className="text-[#020D2C] text-[20px] font-semibold schibsted-grotesk">{feature.title}</h3>
                <p className="text-[16px] text-[#020D2C]/60 font-medium schibsted-grotesk">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#01001E]  py-[60px] relative overflow-hidden">

        <h2 className="text-white z-50 relative text-center text-[32px] md:text-[48px] font-extrabold leading-[120%] font-NeueMontreal">We Make for Your Team  </h2>
        <p className="text-[#c4c4c4] z-50 relative text-center text-[16px] md:text-[18px] font-normal leading-[150%] schibsted-grotesk tracking-wider py-4">Our team ensures timely results without compromising quality</p>

        <section className='flex items-center justify-center gap-3 '>
          <TestiReviewCommonImages />
          <div>
            <a
              href='https://maps.app.goo.gl/wxZr7ckDhrWnVbx27'
              className='text-[#090D28]'
              target='_blank'
            >
              {' '}
              <p className=' m-0 flex items-center gap-1 p-0 pb-[6px] text-center font-LatinkaRegular text-sm font-normal leading-[120%] text-white'>
                <Image className='w-6' src={getImageUrl("/vilvabusiness/website/common/homepage/google-img.webp")} width={50} height={50} quality={75} alt='GoogleImg' />{' '}
                Google Reviews
              </p>
            </a>
            <div className='flex items-center gap-2 text-white'>
              4.7
              <div className='flex items-center gap-2'>
                <SvgIcons.ReviewStar />
                <SvgIcons.ReviewStar />
                <SvgIcons.ReviewStar />
                <SvgIcons.ReviewStar />
                <SvgIcons.ReviewStar />
              </div>
            </div>
          </div>
        </section>
        <div className="relative z-50">


        </div>


      </section>

      <section className="py-20  text-white bg-[#01001E] relative">
        <div className="max-w-7xl mx-auto px-[24px]">
          <div className="space-y-[20px] ">
            <div className="">
              <p className="text-[#F1F6F8] md:text-[28px] text-[24px] font-semibold lg:text-start text-center font-NeueMontreal tracking-wider">Ready to Scale Your Fintech With Compliance & Speed?</p>
              <p className="text-[#F1F6F8]/60 text-[18px] schibsted-grotesk lg:text-start text-center">One demo. Endless possibilities to transform your fintech journey</p>
            </div>
            <div className="lg:text-start text-center">
              <button className="text-white px-[28px] py-[14px] schibsted-grotesk rounded-[8px] bg-[#195AFF]">
                Talk to Our Experts
              </button>
            </div>
          </div>

        </div>
        <div className="absolute bottom-0 right-[10%]">
          {/* Desktop Image */}
          <Image
            src={getImageUrl("/vilvabusiness/website/products/fintech/grap.webp")}
            alt="Fintech Graph"
            width={800}
            height={500}
            className="hidden lg:block w-[300px] h-auto"
            loading="lazy"
            decoding="async"
          />

          {/* Mobile Image */}
          <Image
            src={getImageUrl("/vilvabusiness/website/products/fintech/grab-mob.webp")}
            alt="Fintech Graph"
            width={1194}
            height={954}
            className="block lg:hidden w-[200px] h-auto"
            loading="lazy"
            decoding="async"
          />
        </div>
      </section>

      <section className="md:mb-[100px] mb-[50px]">
        <FaqSection faqs={faqs} />
      </section>

    </div>
  )
}



function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-[#F2F6F8] shadow-inner p-6">
      {children}
    </div>
  );
}

function Title({ children, desc }: { children: React.ReactNode; desc?: React.ReactNode }) {
  return (
    <div className="pb-2">
      <p className="text-xl text-[#020D2C] font-semibold">{children}</p>
      {desc && (
        <p className="text-[#020D2C] opacity-60 text-base leading-6 mt-1">{desc}</p>
      )}
    </div>
  );
}

function Chip({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`inline-flex items-center gap-2 rounded border px-2.5 py-1 text-xs font-medium ${className}`}>
      {children}
    </div>
  );
}







function CustomerPortalsPanel() {
  return (
    <div className="bg-[#F2F6F8] relative rounded-[12px] md:h-[300px] h-[320px]
  transition-all duration-300 ease-in-out
  group-hover/panel:blur-[2px]
  hover:!scale-[1.03] hover:!blur-0 ">
      {/* <Title desc={
        <>We design intuitive portals that simplify complexity and enable seamless interactions. Built for speed and clarity, they drive operational efficiency for businesses.</>
      }>Customer & Admin Portals</Title> */}

      {/* <Chip className="bg-[#D6FDE2] text-[#18970C] border-[#18970C] mb-4">
        <CheckCircle className="w-3 h-3" /> Success
      </Chip>

      <div className="relative">
        <div className="bg-white w-1/2 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="w-4 h-4 rounded-sm bg-[#E7F0FB] border border-[#CCDCF2] flex items-center justify-center">
              <ArrowRight className="w-2.5 h-2.5 text-[#337CE3]" />
            </div>
            <div className="text-xs bg-gray-100 px-2 py-1 rounded flex items-center gap-1">
              <span>Last Month</span>
              <ArrowUp className="w-3 h-3 rotate-90" />
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Total Transactions</div>
            <div className="text-lg font-bold text-gray-800">325</div>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-xs text-[#18970C]">1.5%</span>
            <ArrowUp className="w-3 h-3 text-[#18970C]" />
          </div>
          <div className="bg-gray-100 text-center py-1 rounded text-xs">See More</div>
        </div>

        <div className="bg-white w-1/2 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="w-4 h-4 rounded-sm bg-[#FBF3E7] border border-[#F2E2CC] flex items-center justify-center">
              <DollarSign className="w-2.5 h-2.5 text-[#FF9E15]" />
            </div>
            <div className="text-xs bg-gray-100 px-2 py-1 rounded flex items-center gap-1">
              <span>Last Month</span>
              <ArrowUp className="w-3 h-3 rotate-90" />
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Total Earnings</div>
            <div className="text-sm font-bold text-gray-800">₹ 2,40,000</div>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-xs text-[#18970C]">1.5%</span>
            <ArrowUp className="w-3 h-3 text-[#18970C]" />
          </div>
          <div className="bg-gray-100 text-center py-1 rounded text-xs">See More</div>
        </div>
      </div> */}

      <div className="space-y-[8px] p-[24px] ">
        <p className="text-[#020D2C] text-[20px] font-semibold font-NeueMontreal">Customer & Admin Portals</p>
        <p className="text-[#020D2C]/60 text-[16px] schibsted-grotesk">We design intuitive portals that simplify complexity and enable seamless interactions. Built for speed and clarity, they drive operational efficiency for businesses.</p>
      </div>
      {/* <Image className="md:max-w-[300px] max-w-[250px] absolute bottom-[24px] right-[24px]" src={img1} alt="image tag" /> */}
      <Image
        src={getImageUrl("/vilvabusiness/website/products/fintech/img1.webp")}
        alt="Fintech customer portal dashboard"
        width={800}
        height={500}
        className="md:max-w-[300px] max-w-[250px] absolute bottom-[24px] right-[24px]"
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

function BusinessIntelligencePanel() {
  // const chartData = [
  //   { month: "Jan", value: 25 },
  //   { month: "Feb", value: 15 },
  //   { month: "Mar", value: 35 },
  //   { month: "Apr", value: 25 },
  //   { month: "Jun", value: 20 },
  //   { month: "Jul", value: 15 },
  //   { month: "Aug", value: 32 },
  //   { month: "Sep", value: 15 },
  //   { month: "Oct", value: 15 },
  //   { month: "Nov", value: 28 },
  //   { month: "Dec", value: 20 },
  // ];

  return (
    <div className="bg-[#F2F6F8] relative rounded-[12px] md:h-[300px] h-[320px]
  transition-all duration-300 ease-in-out
  group-hover/panel:blur-[2px]
  hover:!scale-[1.03] hover:!blur-0 " >
      {/* <Title desc={
        <>Transform complex data into real-time insights, empowering smarter decisions and driving accelerated growth.</>
      }>Business Intelligence Dashboards</Title> */}

      {/* <div className="bg-white rounded-lg p-4">
        <div className="text-sm font-semibold text-gray-800 mb-4">Transactions Overview</div>
        <div className="space-y-4">
          <div className="flex items-end justify-between h-32 gap-1">
            {chartData.map((item, index) => (
              <div key={item.month} className="flex flex-col items-center flex-1">
                <div
                  className={`w-full rounded-sm ${index < 6 ? "bg-[#7FAFF3]" : "bg-[#CBDBF2]"} mb-2`}
                  style={{ height: `${item.value * 3}px` }}
                />
                <span className="text-xs text-gray-500">{item.month}</span>
              </div>
            ))}
          </div>
          <div className="relative">
            <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded">
              ₹ 32,100
            </div>
          </div>
        </div>
      </div> */}
      <div className="space-y-[8px] p-[24px] ">
        <p className="text-[#020D2C] text-[20px] font-semibold font-NeueMontreal">Business Intelligence Dashboards</p>
        <p className="text-[#020D2C]/60 text-[16px] schibsted-grotesk">Transform complex data into real-time insights, empowering smarter decisions and driving accelerated growth.</p>
      </div>
      {/* <Image className="md:max-w-[300px] max-w-[250px] absolute bottom-[0px] right-[0px]" src={img2} alt="image tag" /> */}
      <Image
        src={getImageUrl("/vilvabusiness/website/products/fintech/img2.webp")}
        alt="Fintech business intelligence and analytics dashboard"
        width={800}
        height={500}
        className="md:max-w-[300px] max-w-[250px] absolute bottom-[0px] right-[0px]"
        loading="lazy"
        decoding="async"
      />
    </div>

  );
}

function DigitalOnboardingPanel() {
  return (
    <div className="bg-[#F2F6F8] relative rounded-[12px] md:h-[300px] h-[320px]
  transition-all duration-300 ease-in-out
  group-hover/panel:blur-[2px]
  hover:!scale-[1.03] hover:!blur-0 ">
      {/* <Title desc={
        <>Seamless onboarding made simple. Automated KYC, CIBIL checks, and lending verification. Instant disbursals for a faster experience.</>
      }>Digital Onboarding Suites</Title> */}

      {/* <div className="space-y-3 mb-4">
        <Chip className="bg-white text-gray-800 border-gray-200">
          <CheckCircle className="w-3 h-3 text-[#18970C]" /> CIBIL Completed
        </Chip>
        <Chip className="bg-white text-gray-800 border-gray-200">
          <CheckCircle className="w-3 h-3 text-[#18970C]" /> KYC Completed
        </Chip>
      </div>

      <div className="bg-white rounded-lg p-4 space-y-4">
        <div className="text-center text-sm text-gray-500">Log In</div>
        <div className="space-y-3">
          <div className="h-10 bg-gray-100 rounded" />
          <div className="h-2 bg-gray-100 rounded w-3/4" />
          <div className="h-2 bg-gray-100 rounded w-1/2" />
          <button className="w-full bg-[#7FAFF3] text-white py-2 rounded text-sm">Log In to Dashboard</button>
          <div className="h-2 bg-gray-100 rounded w-4/5" />
        </div>
      </div> */}
      <div className="space-y-[8px] p-[24px] ">
        <p className="text-[#020D2C] text-[20px] font-semibold font-NeueMontreal">Digital Onboarding Suites</p>
        <p className="text-[#020D2C]/60 text-[16px] schibsted-grotesk">Seamless onboarding made simple. Automated KYC, CIBIL checks, and lending verification. Instant disbursals for a faster experience.</p>
      </div>
      {/* <Image className="md:max-w-[300px] max-w-[250px] absolute bottom-0 right-[40px]" src={img3} alt="image tag" /> */}
      <Image
        src={getImageUrl("/vilvabusiness/website/products/fintech/img3.webp")}
        alt="Fintech digital customer onboarding and KYC flow"
        width={800}
        height={500}
        className="md:max-w-[300px] max-w-[250px] absolute bottom-0 right-[40px]"
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

function AuditCompliancePanel() {
  return (
    <div className="bg-[#F2F6F8] relative rounded-[12px] md:h-[300px] h-[320px]
  transition-all duration-300 ease-in-out
  group-hover/panel:blur-[2px]
  hover:!scale-[1.03] hover:!blur-0 ">
      {/* <Title desc={
        <>TCI / ISO Audits<br/>Cybersecurity & DLSIR Audits<br/>CERT-In Panelled Audit & Consulting</>
      }>Audit & Compliance</Title> */}

      {/* <div className="space-y-3 mb-4">
        <Chip className="bg-[#5089D9] text-white border-[#5089D9]">ISO Audit</Chip>
        <Chip className="bg-white text-gray-800 border">Cybersecurity Audit</Chip>
        <Chip className="bg-white text-gray-800 border opacity-50">DLSIR Audit</Chip>
      </div>

      <div className="flex items-center justify-center">
        <div className="relative w-32 h-32">
          <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="50" stroke="#DBE4DA" strokeWidth="10" fill="none" />
            <circle cx="60" cy="60" r="50" stroke="#18970C" strokeWidth="10" fill="none" strokeDasharray={`${2 * Math.PI * 50 * 0.8} ${2 * Math.PI * 50}`} strokeLinecap="round" />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-800">80%</div>
              <div className="text-sm text-gray-500">Completed</div>
            </div>
          </div>
        </div>
      </div> */}
      <div className="space-y-[8px] p-[24px] ">
        <p className="text-[#020D2C] text-[20px] font-semibold font-NeueMontreal">Audit & Compliance</p>
        <ul className="px-[24px] list-disc space-y-1">
          <li className="text-[#020D2C]/60 text-[16px] schibsted-grotesk">
            TCI / ISO Audits
          </li>
          <li className="text-[#020D2C]/60 text-[16px] schibsted-grotesk">
            Cybersecurity & DLSIR Audits
          </li>
          <li className="text-[#020D2C]/60 text-[16px] schibsted-grotesk">
            CERT-In Panelled Audit & Consulting
          </li>
        </ul>
        {/* <p className="text-[#020D2C]/60 text-[16px] schibsted-grotesk">We design intuitive portals that simplify complexity and enable seamless interactions. Built for speed and clarity, they drive operational efficiency for businesses.</p> */}
      </div>
      {/* <Image className="md:max-w-[300px] max-w-[250px] absolute bottom-[24px] right-[24px]" src={img4} alt="image tag" /> */}
      <Image
        src={getImageUrl("/vilvabusiness/website/products/fintech/img4.webp")}
        alt="Fintech audit and compliance dashboard"
        width={800}
        height={500}
        className="md:max-w-[300px] max-w-[250px] absolute bottom-[24px] right-[24px]"
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

function ConsultingLicensingPanel({ isMobile }: { isMobile: boolean }) {
  const blocks = Array.from({ length: 36 });
  return (
    <div className="bg-[#F2F6F8] relative rounded-[12px] md:h-[300px] h-[320px]
  transition-all duration-300 ease-in-out
  group-hover/panel:blur-[2px]
  hover:!scale-[1.03] hover:!blur-0 ">
      {/* <Title desc={
        <>PA / PG Licensing Consulting<br/>NBFC Onboarding & Consulting<br/>P2P Lending Compliance</>
      }>Consulting & Licensing</Title> */}

      {/* <div className="grid grid-cols-6 gap-1 mb-6">
  {blocks.map((_, i) => (
    <div
      key={i}
      className={`aspect-square rounded h-[40px] w-[40px] ${
        i < 6 ? 'bg-gradient-to-br from-[#7DACEE] to-[#418AF1]' : // Top row fully colored
        i < 12 ? 'bg-gradient-to-br from-[#7DACEE] to-[#418AF1] opacity-75' : // Second row
        i < 18 ? 'bg-gradient-to-br from-[#7DACEE] to-[#418AF1] opacity-50' : // Third row
        i < 24 ? 'bg-gradient-to-br from-[#7DACEE] to-[#418AF1] opacity-25' : // Fourth row
        'bg-gradient-to-br from-[#7DACEE] to-[#418AF1] opacity-10' // Bottom rows
      }`}
    />
  ))}
</div>

      <div className="text-center text-xl font-bold text-[#6CA4F3]">+80%</div> */}

      <div className="space-y-[8px] p-[24px] ">
        <p className="text-[#020D2C] text-[20px] font-semibold font-NeueMontreal">Consulting & Licensing</p>
        <ul className="space-y-2 px-[24px]">
          <li className="text-[#020D2C]/60 text-[16px] schibsted-grotesk">
            PA / PG Licensing Consulting
          </li>

          <li className="text-[#020D2C]/60 text-[16px] schibsted-grotesk">
            NBFC Onboarding & Consultings
          </li>

          <li className="text-[#020D2C]/60 text-[16px] schibsted-grotesk">
            P2P Lending Compliance
          </li>
        </ul>
        {/* <p className="text-[#020D2C]/60 text-[16px] schibsted-grotesk">We design intuitive portals that simplify complexity and enable seamless interactions. Built for speed and clarity, they drive operational efficiency for businesses.</p> */}
      </div>
      {/* <Image className="md:max-w-[300px] max-w-[250px] absolute bottom-0 right-0" src={img5} alt="image tag" /> */}
      <Image
        src={
          isMobile
            ? getImageUrl("/vilvabusiness/website/products/fintech/img5_1_1-Mobile.webp")
            : getImageUrl("/vilvabusiness/website/products/fintech/img5_1_2-Desktop.webp")
        }
        alt="Fintech consulting and licensing workflow"
        width={isMobile ? 250 : 800}
        height={isMobile ? 250 : 500}
        className="md:max-w-[300px] max-w-[250px] absolute bottom-0 right-0"
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

function DisbursalCollectionPanel() {
  // const transactions = [
  //   { id: 1, paymentId: "TD123456", customer: "John Doe", mode: "Credited", dateTime: "20 Aug 2025 - 12:45 AM", amount: "₹ 32,100", status: "Success", method: "UPI" },
  //   { id: 2, paymentId: "TD123456", customer: "Alex Smith", mode: "Debited", dateTime: "15 Sep 2026 - 03:30 PM", amount: "₹ 320", status: "Failed", method: "Credit Card" },
  //   { id: 3, paymentId: "TD123456", customer: "Emma Johnson", mode: "Credited", dateTime: "10 Oct 2024 - 09:00 AM", amount: "₹ 2000", status: "Pending", method: "Debit Card" },
  //   { id: 4, paymentId: "TD123456", customer: "Liam Brown", mode: "Credited", dateTime: "5 Nov 2023 - 06:15 PM", amount: "₹ 32,100", status: "Success", method: "UPI" },
  // ];

  return (
    <div className="bg-[#F2F6F8] relative rounded-[12px] md:h-[300px] h-[320px]
  transition-all duration-300 ease-in-out
  group-hover/panel:blur-[2px]
  hover:!scale-[1.03] hover:!blur-0 ">
      {/* <Title desc={
        <>Comprehensive solutions from start to finish. Fast, secure, and fully compliant processes. Streamlined loan disbursals and collections.</>
      }>Disbursal & Collection Solutions</Title> */}


      {/* <div className="flex gap-2 mb-4 items-center">
          <div className="flex bg-gray-100 rounded p-1">
            <button className="bg-white px-3 py-1 rounded text-sm font-medium">
              Completed <span className="bg-gray-200 px-1 rounded ml-1">16</span>
            </button>
            <button className="px-3 py-1 text-sm text-gray-600">
              Pending <span className="bg-gray-300 px-1 rounded ml-1">16</span>
            </button>
          </div>
          <div className="ml-auto flex gap-2">
            <div className="flex items-center gap-2 border rounded px-3 py-1">
              <Search className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500">Search</span>
            </div>
            <button className="flex items-center gap-2 border rounded px-3 py-1">
              <Filter className="w-4 h-4" />
              <span className="text-sm">Filter</span>
            </button>
          </div>
        </div>


        <div className="rounded-md overflow-hidden border">

          <div
            className="grid bg-[#F4F5F7] text-xs font-medium text-gray-600 px-4 py-3"
            style={{ gridTemplateColumns: "40px 110px 130px 100px 200px 110px 110px 110px" }}
          >
            <div>SI No</div>
            <div>Payment ID</div>
            <div>Customer</div>
            <div>Mode</div>
            <div>Date & Time</div>
            <div>Amount</div>
            <div>Status</div>
            <div>Method</div>
          </div>


          {transactions.map((t, i) => (
            <div
              key={t.id}
              className="grid items-center px-4 py-3 border-t text-xs bg-white"
              style={{ gridTemplateColumns: "40px 110px 130px 100px 200px 110px 110px 110px" }}
            >
              <div>{i + 1}</div>
              <div>{t.paymentId}</div>
              <div>{t.customer}</div>
              <div>{t.mode}</div>
              <div>{t.dateTime}</div>
              <div>{t.amount}</div>
              <div>
                <div
                  className={`inline-flex items-center rounded px-2.5 py-1 border ${
                    t.status === "Success"
                      ? "bg-[#D6FDE2] text-[#23B04E] border-[#23B04E]"
                      : t.status === "Failed"
                      ? "bg-[#FEDBDB] text-[#EC2020] border-[#EC2020]"
                      : "bg-[#FDEBD7] text-[#F8921E] border-[#F8921E]"
                  }`}
                >
                  {t.status}
                </div>
              </div>
              <div>{t.method}</div>
            </div>
          ))}
        </div> */}
      <div className="space-y-[8px] p-[24px] ">
        <p className="text-[#020D2C] text-[20px] font-semibold font-NeueMontreal">Disbursal & Collection Solutions</p>
        <p className="text-[#020D2C]/60 text-[16px] schibsted-grotesk">Comprehensive solutions from start to finish. Fast, secure, and fully compliant processes. Streamlined loan disbursals and collections.</p>
      </div>
      {/* <Image className="md:max-w-[300px] max-w-[250px] absolute bottom-[0px] right-[0px]" src={img6} alt="image tag" /> */}
      <Image
        src={getImageUrl("/vilvabusiness/website/products/vilvad2c/img6_fintech_2-mobile.webp")}
        alt="Fintech loan disbursal and collection dashboard on mobile"
        width={800}
        height={500}
        className="max-w-[250px] absolute bottom-[0px] right-[0px] md:hidden"
        loading="lazy"
        decoding="async"
      />

      <Image
        src={getImageUrl("/vilvabusiness/website/products/vilvad2c/img6_fintech_1-desktop.webp")}
        alt="Fintech loan disbursal and collection dashboard"
        width={800}
        height={500}
        className="hidden md:block md:max-w-[300px] lg:max-w-[400px] absolute bottom-[0px] right-[0px]"
        loading="lazy"
        decoding="async"
      />
    </div>

  );
}



export default Fintech